import { Option } from "@inrev/core";
import {
	type DataNeeded,
	dataNeededQueryKey,
	sortDataNeeded,
	usePendingIds,
} from "@portal/api/decisioning";
import {
	useDeleteDataNeeded,
	useReopenDataNeeded,
	useResolveDataNeeded,
} from "@portal/api/decisioning/admin";
import { type FC, useMemo, useState } from "react";
import { Disclosure } from "../../../../patterns/disclosure";
import { EmptyState } from "../../../../patterns/empty-state";
import { RetryableLoadError } from "../../../../patterns/retryable-load-error";
import { ActionMenu, Button, ClickableText, Text } from "../../../../primitives";
import { formatDecisioningDate } from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import { useDecisioningPanel } from "../DecisioningPanelContext";
import { ItemActionMenu } from "../ItemActionMenu";
import { DataNeededFulfillment } from "./DataNeededFulfillment";
import { DeleteRequestConfirmation } from "./DeleteRequestConfirmation";
import { RequestCard } from "./RequestCard";

const ResolvedRequestRow: FC<{
	item: DataNeeded;
	onReopen: () => void;
	onDelete: () => void;
}> = ({ item, onReopen, onDelete }) => {
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const { title } = item;
	const testId = `data-needed-resolved-${item.id}`;
	const when = item.resolvedAt ? ` ${formatDecisioningDate(item.resolvedAt)}` : "";
	const outcome =
		item.status === "received"
			? `Received${when}`
			: `Closed${when}${item.closedReason ? ` (${item.closedReason})` : ""}`;

	return (
		<Disclosure.Row
			testId={testId}
			className="group/item items-start"
			trailing={
				<div className="flex items-center gap-1">
					<ClickableText
						type="caption"
						color="link"
						testId={`${testId}-reopen`}
						aria-label={`Reopen ${title}`}
						onClick={onReopen}
					>
						Reopen
					</ClickableText>
					<ItemActionMenu itemName={title} testId={`${testId}-menu`}>
						<ActionMenu.Item
							tone="danger"
							onClick={() => setConfirmingDelete(true)}
							data-testid={`${testId}-delete`}
						>
							Delete
						</ActionMenu.Item>
					</ItemActionMenu>
				</div>
			}
		>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<Text type="body" color="primary" truncate className="block font-medium">
					{title}
				</Text>
				<Text type="caption" color="tertiary" className="block [text-box-trim:none]">
					{outcome}
				</Text>
				<DataNeededFulfillment
					attachmentIds={item.fulfillmentAttachmentIds}
					answer={item.fulfillmentAnswer}
					testId={`${testId}-fulfillment`}
				/>
			</div>
			{confirmingDelete && (
				<DeleteRequestConfirmation
					resolved
					title={title}
					testId={testId}
					onCancel={() => setConfirmingDelete(false)}
					onConfirm={() => {
						setConfirmingDelete(false);
						onDelete();
					}}
				/>
			)}
		</Disclosure.Row>
	);
};

const ResolvedRequests: FC = () => {
	const { accountId } = useDecisioningPanel();
	const { resolvedDataNeeded, resolvedLoadError } = useDecisioningData();
	const { reopenDataNeeded } = useReopenDataNeeded(accountId);
	const { deleteDataNeeded } = useDeleteDataNeeded(accountId);
	const [open, setOpen] = useState(false);

	if (Option.isSome(resolvedLoadError)) {
		return (
			<RetryableLoadError
				resource="received and closed requests"
				onRetry={resolvedLoadError.retry}
				testId="data-needed-resolved-load-error"
			/>
		);
	}
	if (resolvedDataNeeded.length === 0) return null;

	return (
		<Disclosure open={open} onOpenChange={setOpen} testId="data-needed-resolved">
			<Disclosure.Summary testId="data-needed-resolved-summary">
				Received and closed ({resolvedDataNeeded.length})
			</Disclosure.Summary>
			<Disclosure.Panel>
				{resolvedDataNeeded.map((item) => (
					<ResolvedRequestRow
						key={item.id}
						item={item}
						onReopen={() => reopenDataNeeded(item.id)}
						onDelete={() => deleteDataNeeded(item.id)}
					/>
				))}
			</Disclosure.Panel>
		</Disclosure>
	);
};

/** Open requests, most important first, plus the received and closed trail. */
export const DataNeededTab: FC = () => {
	const { accountId, openSheet } = useDecisioningPanel();
	const { dataNeeded, proConsById, resolvedDataNeeded, resolvedLoadError } = useDecisioningData();
	const { resolveDataNeeded } = useResolveDataNeeded(accountId);
	const { deleteDataNeeded } = useDeleteDataNeeded(accountId);
	const pendingIds = usePendingIds(dataNeededQueryKey(accountId));
	const items = useMemo(() => sortDataNeeded(dataNeeded), [dataNeeded]);

	const emptyMessage = Option.isSome(resolvedLoadError)
		? "Nothing open."
		: resolvedDataNeeded.length === 0
			? "Nothing requested yet. Request data from any factor, or here."
			: "Nothing open. Everything requested has been received or closed.";

	return (
		<div className="flex flex-col gap-3" data-testid="decisioning-data-needed">
			<div className="flex items-center justify-between gap-2">
				<Text type="caption" color="tertiary" data-testid="data-needed-count">
					{items.length} open, most important first
				</Text>
				<Button
					variant="ghost"
					size="sm"
					className="text-link"
					onClick={() => openSheet({ kind: "request", mode: "add" })}
					data-testid="decisioning-data-needed-add"
				>
					Request data
				</Button>
			</div>

			{items.length === 0 ? (
				<EmptyState message={emptyMessage} className="py-6" testId="data-needed-empty" />
			) : (
				<div className="grid grid-cols-[repeat(auto-fit,minmax(296px,1fr))] gap-2">
					{items.map((item) => (
						<RequestCard
							key={item.id}
							item={item}
							linkedProCon={item.proConId ? proConsById.get(item.proConId) : undefined}
							pending={pendingIds.has(item.id)}
							onReceive={() => openSheet({ kind: "resolve", dataNeededId: item.id })}
							onClose={(closedReason) =>
								resolveDataNeeded({ id: item.id, resolution: { status: "closed", closedReason } })
							}
							onDelete={() => deleteDataNeeded(item.id)}
						/>
					))}
				</div>
			)}

			<ResolvedRequests />
		</div>
	);
};
